import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { UpgradeProvider } from './components/UpgradeModal';
import { Layout } from './components/Layout';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import WorkboardPage from './pages/WorkboardPage';
import IntakePage from './pages/IntakePage';
import JobDetailPage from './pages/JobDetailPage';
import TechJobsPage from './pages/TechJobsPage';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import PublicEstimatePage from './pages/PublicEstimatePage';
import PublicBillingPage from './pages/PublicBillingPage';
import PublicTrackingPage from './pages/PublicTrackingPage';
import AdminPage from './pages/AdminPage';
import AdminShopDetailPage from './pages/AdminShopDetailPage';
import './App.css';

function FullLoader() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-[hsl(var(--background))]">
      <div className="animate-pulse text-sm text-[hsl(var(--muted-foreground))]">Loading…</div>
    </div>
  );
}

function Protected({ children, roles }) {
  const { me, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.user.is_platform_admin) return <Navigate to="/admin" replace />;
  if (roles && !roles.includes(me.user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AdminProtected({ children }) {
  const { me, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!me) return <Navigate to="/login" replace />;
  if (!me.user.is_platform_admin) return <Navigate to="/" replace />;
  return children;
}

function Home() {
  const { me, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.user.is_platform_admin) return <Navigate to="/admin" replace />;
  if (me.user.role === 'technician') return <Navigate to="/tech" replace />;
  if (me.user.role === 'owner') return <Navigate to="/dashboard" replace />;
  return <Navigate to="/board" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UpgradeProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/e/:token" element={<PublicEstimatePage />} />
            <Route path="/b/:token" element={<PublicBillingPage />} />
            <Route path="/t/:token" element={<PublicTrackingPage />} />
            <Route path="/board" element={<Protected roles={['owner', 'advisor']}><WorkboardPage /></Protected>} />
            <Route path="/intake" element={<Protected roles={['owner', 'advisor']}><IntakePage /></Protected>} />
            <Route path="/jobs/:jobId" element={<Protected><JobDetailPage /></Protected>} />
            <Route path="/billing/:jobId" element={<Protected roles={['owner', 'advisor']}><BillingPage /></Protected>} />
            <Route path="/tech" element={<Protected roles={['technician']}><TechJobsPage /></Protected>} />
            <Route path="/dashboard" element={<Protected roles={['owner']}><DashboardPage /></Protected>} />
            <Route path="/settings" element={<Protected roles={['owner']}><SettingsPage /></Protected>} />
            <Route path="/admin" element={<AdminProtected><AdminPage /></AdminProtected>} />
            <Route path="/admin/shops/:shopId" element={<AdminProtected><AdminShopDetailPage /></AdminProtected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </UpgradeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
